import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';
import { UserService } from './user.service';
import { ToggleService } from './toggle.service';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  availableLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private userService: UserService,
    private toggleService: ToggleService,
    public http: HttpClient,
  ) {
    this.initializeLanguage();
  }

  private async initializeLanguage() {
    try {
      // Check if internationalization is enabled
      const isInternationalizationEnabled = this.toggleService.isToggleEnabled('internationalization');
      
      if (!isInternationalizationEnabled) {
        // If internationalization is disabled, always use English
        this.translate.use('en');
        this.currentLanguageSubject.next('en');
        return;
      }

      let language = 'en'; // default fallback
      
      // Try to get language from API if user is logged in
      if (this.userService.LoggedIn()) {
        try {
          const userData = await this.userService.updateLoginData();
          if (userData && userData.preferredLanguage) {
            language = userData.preferredLanguage;
          } else {
            console.log('LanguageService: No preferred language found in API data');
          }
        } catch (error) {
          console.warn('LanguageService: Failed to fetch language from API, falling back to local storage:', error);
          // Continue with local storage fallback
        }
      } else {
        console.log('LanguageService: User not logged in, skipping API language fetch');
      }
      
      // If no language from API, check local storage
      if (language === 'en') {
        try {
          console.log('LanguageService: Checking local storage for saved language');
          const savedLanguage = await this.storageService.get('preferredLanguage');
          if (savedLanguage && this.availableLanguages.some(l => l.code === savedLanguage)) {
            language = savedLanguage;
            console.log('LanguageService: Language from local storage:', language);
          } else {
            console.log('LanguageService: No valid language found in local storage');
          }
        } catch (storageError) {
          console.warn('LanguageService: Error accessing local storage, using default language:', storageError);
          // Continue with default language
        }
      }
      
      // Validate language is supported
      if (!this.availableLanguages.some(l => l.code === language)) {
        console.log('LanguageService: Language not supported, falling back to English:', language);
        language = 'en';
      }
      
      this.translate.use(language);
      this.currentLanguageSubject.next(language);
    } catch (error) {
      console.error('LanguageService: Error initializing language:', error);
      // Fallback to English
      console.log('LanguageService: Falling back to English due to error');
      this.translate.use('en');
      this.currentLanguageSubject.next('en');
    }
  }

  async changeLanguage(languageCode: string) {
    // Check if internationalization is enabled
    const isInternationalizationEnabled = this.toggleService.isToggleEnabled('internationalization');
    
    if (!isInternationalizationEnabled) {
      console.warn('LanguageService: Internationalization is disabled. Language cannot be changed.');
      return;
    }

    if (this.availableLanguages.some(l => l.code === languageCode)) {
      this.translate.use(languageCode);
      this.currentLanguageSubject.next(languageCode);
      
      // Save to local storage as backup
      try {
        await this.storageService.set('preferredLanguage', languageCode);
      } catch (storageError) {
        console.warn('LanguageService: Failed to save language to local storage:', storageError);
        // Continue with API save attempt
      }
      
      // Save to API if user is logged in
      if (this.userService.LoggedIn()) {
        try {
          await this.saveLanguageToAPI(languageCode);
        } catch (error) {
          console.warn('LanguageService: Failed to save language to API:', error);
          // Language is still saved locally, so the change is still effective
        }
      } else {
        console.log('LanguageService: User not logged in, skipping API save');
      }
    } else {
      console.warn('LanguageService: Invalid language code:', languageCode);
    }
  }

  private async saveLanguageToAPI(languageCode: string): Promise<void> {
    try {
      // Check if user service is available and user is still logged in
      if (!this.userService.LoggedIn()) {
        throw new Error('User not logged in');
      }
      
      const response = await this.http.put(
        `${environment.claimApi}/users/language`,
        { language: languageCode },
        { headers: this.userService.genericHeaders() }
      ).toPromise();
      
      if (response) {
        console.log('LanguageService: Language preference saved to API successfully');
      } else {
        throw new Error('Failed to save language preference - no response');
      }
    } catch (error) {
      // Handle specific error types
      if (error.status === 401) {
        console.warn('LanguageService: Unauthorized - user may need to re-authenticate');
      } else if (error.status === 403) {
        console.warn('LanguageService: Forbidden - user may not have permission to update language');
      } else if (error.status >= 500) {
        console.warn('LanguageService: Server error - API may be temporarily unavailable');
      }
      
      throw error;
    }
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  getCurrentLanguageInfo() {
    const currentLang = this.getCurrentLanguage();
    return this.availableLanguages.find(l => l.code === currentLang) || this.availableLanguages[0];
  }

  /**
   * Check if internationalization is enabled
   */
  isInternationalizationEnabled(): boolean {
    return this.toggleService.isToggleEnabled('internationalization');
  }

  /**
   * Get available languages based on internationalization toggle
   */
  getAvailableLanguages() {
    const isInternationalizationEnabled = this.toggleService.isToggleEnabled('internationalization');
    
    if (!isInternationalizationEnabled) {
      // If internationalization is disabled, only return English
      return this.availableLanguages.filter(l => l.code === 'en');
    }
    
    return this.availableLanguages;
  }
} 